import { Activity, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { useDialog } from '../lib/useDialog';

const KEY = 'watch.sidebar';

const storedRail = (): boolean => {
  try {
    return localStorage.getItem(KEY) === 'rail';
  } catch {
    // A private window or blocked site data must not stop the app rendering.
    return false;
  }
};

interface Props {
  /** What the environment badge says, and its `wt-env-*` modifier. */
  env: { label: string; kind: string };
  /** The screen links, already filtered to what this person can open. */
  nav: ReactNode;
  /** Who is signed in, and the controls beside them. */
  who?: ReactNode;
  /** Theme, sign-out: icon buttons that stay visible in the rail. */
  tools?: ReactNode;
  /** People waiting across both queues, shown on the phone's menu button. */
  waiting?: number;
  children: ReactNode;
}

/**
 * The console's frame: a sidebar of screens beside the page.
 *
 * Shared by the live console and the fixture preview, so the harness shows the
 * chrome somebody actually reads rather than an approximation of it. The rail
 * (icons only) is a per-browser choice, like the theme; under 760px the sidebar
 * is a drawer behind a menu button instead, and the rail does not apply.
 */
export function Shell({ env, nav, who, tools, waiting = 0, children }: Props) {
  const [rail, setRail] = useState(storedRail);
  const [open, setOpen] = useState(false);

  // On a phone the open sidebar is a modal drawer: focus moves into it, Tab
  // stays inside, Escape closes it, and focus goes back to the menu button.
  const side = useRef<HTMLElement>(null);
  useDialog(side, () => setOpen(false), open);

  const toggleRail = () => {
    const next = !rail;
    setRail(next);
    try {
      if (next) localStorage.setItem(KEY, 'rail');
      else localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  };

  // The environment is stated, never inferred from the URL. A console that
  // looks identical on UAT and prod is how somebody acknowledges a real
  // incident believing they are on staging -- so it is in the sidebar, the
  // rail, and the phone's top bar alike.
  const badge = <span className={`wt-env wt-env-${env.kind}`}>{env.label}</span>;
  const brand = (
    <span className="wt-brand">
      <span className="wt-mark" aria-hidden="true">
        <Activity size={15} strokeWidth={2.5} />
      </span>
      <span className="wt-brand-name">sphere-watch</span>
    </span>
  );

  return (
    <div className="wt-shell" data-rail={rail || undefined} data-open={open || undefined}>
      <aside
        ref={side}
        className="wt-side"
        id="wt-side"
        role={open ? 'dialog' : undefined}
        aria-modal={open || undefined}
        aria-label={open ? 'Screens' : undefined}
      >
        <div className="wt-side-head">
          {brand}
          {badge}
        </div>
        {/* Following a link closes the phone drawer; on a desktop it is
            already closed and this does nothing. */}
        <nav
          className="wt-side-nav"
          aria-label="Screens"
          onClick={(event) => {
            if ((event.target as Element).closest('a')) setOpen(false);
          }}
        >
          {nav}
        </nav>
        <div className="wt-side-foot">
          {who && <div className="wt-side-who">{who}</div>}
          <div className="wt-side-tools">
            {tools}
            <span className="wt-spacer" />
            <button
              type="button"
              className="btn btn-icon wt-side-collapse"
              title={rail ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!rail}
              aria-controls="wt-side"
              onClick={toggleRail}
            >
              {rail ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
            </button>
          </div>
        </div>
      </aside>
      {open && <div className="wt-side-backdrop" onClick={() => setOpen(false)} />}
      <div className="wt-body">
        <header className="wt-mobilebar">
          <button
            type="button"
            className="btn btn-icon wt-menu"
            aria-label={waiting > 0 ? `Screens, ${waiting} waiting` : 'Screens'}
            aria-expanded={open}
            aria-controls="wt-side"
            onClick={() => setOpen(true)}
          >
            <Menu size={17} />
            {waiting > 0 && <span className="wt-menu-dot" />}
          </button>
          {brand}
          {badge}
        </header>
        <main className="wt-main">{children}</main>
      </div>
    </div>
  );
}

/** One sidebar entry: icon, label, and a count only when somebody is waiting. */
export function NavItem({
  icon: Icon,
  label,
  count,
  countTitle,
}: {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  count?: number;
  countTitle?: string;
}) {
  return (
    <>
      <Icon size={17} />
      <span className="wt-side-label">{label}</span>
      {/* A badge that is always present stops being read. */}
      {count ? (
        <span className="wt-nav-badge" title={countTitle}>
          {count}
        </span>
      ) : null}
    </>
  );
}
